export interface KeyMap {
  throttle: string[];
  brake: string[];
  steerLeft: string[];
  steerRight: string[];
  handbrake: string[];
  clutch: string[];
  blinkerLeft: string;
  blinkerRight: string;
  hazard: string;
  shoulderLeft: string;
  shoulderRight: string;
  lookInterior: string;
  lookExterior: string;
  lookExteriorRight?: string;
  cameraToggle: string;
  horn: string;
  engineRestart: string;
  seatbelt: string;
  /** Manual: shift up one gear (no clutch needed in auto) */
  gearUp: string;
  /** Manual clutch + gear down; in Automatic: cycles D→N→R→P — separate key to avoid clutch conflict */
  gearDown: string;
}

export type WheelPresetId = 'hori_pro_dx' | 'logitech_g29' | 'thrustmaster' | 'custom';
export type WheelPaddleMode = 'blinkers' | 'gears';
export type WheelPedalMode = 'auto' | 'triggers' | 'separate_axes' | 'combined_axis';

export interface WheelMappingItem {
  type: 'axis' | 'button';
  index: number;
  invert?: boolean;
  minVal?: number; // unpressed value
  maxVal?: number; // fully pressed value
}

export interface WheelConfig {
  enabled: boolean;
  presetId: WheelPresetId;
  deviceName: string;
  gamepadIndex: number;
  // HORI Pro DX Hardware Modes
  rotationDegrees: 270 | 180 | 900;
  sensitivityLevel: number; // 1..7 (HORI 7-level adjustable sensitivity)
  paddleMode: WheelPaddleMode; // 'blinkers' (L/R paddles = blinkers) or 'gears' (L/R paddles = shift)
  pedalMode: WheelPedalMode; // 'auto' | 'triggers' | 'separate_axes' | 'combined_axis'
  // Steering
  steerAxis: number;
  steerInvert: boolean;
  steerDeadzone: number;
  steerLinearity: number; // 1.0 = linear, 1.5 = progressive
  // Detachable Pedals (Gas & Brake)
  throttle: WheelMappingItem;
  throttleDeadzone: number;
  brake: WheelMappingItem;
  brakeDeadzone: number;
  clutch?: WheelMappingItem;
  clutchDeadzone?: number;
  // Buttons / Paddles / StVO Observation Controls
  buttonGearUp?: number;
  buttonGearDown?: number;
  buttonBlinkerLeft?: number;
  buttonBlinkerRight?: number;
  buttonHazard?: number;
  buttonHandbrake?: number;
  buttonSeatbelt?: number;
  buttonHorn?: number;
  buttonCamera?: number;
  buttonEngine?: number;
  buttonShoulderLeft?: number;
  buttonShoulderRight?: number;
  buttonLookInterior?: number;
  buttonLookExteriorLeft?: number;
  buttonLookExteriorRight?: number;
}

export interface WheelTelemetry {
  connected: boolean;
  deviceName: string;
  presetId: WheelPresetId;
  steeringAngleDeg: number;
  rawSteering: number;
  throttlePct: number;
  brakePct: number;
  clutchPct: number;
  rotationDegrees: 270 | 180 | 900;
  sensitivityLevel: number;
  paddleMode: WheelPaddleMode;
  pedalMode: WheelPedalMode;
}

export function sensitivityLevelToLinearity(level: number): number {
  const clamped = Math.max(1, Math.min(7, Math.round(level)));
  // Level 1: 1.60 (gentle center), Level 4: 1.00 (1:1 linear), Level 7: 0.70 (quick response)
  const table: Record<number, number> = {
    1: 1.6,
    2: 1.4,
    3: 1.2,
    4: 1.0,
    5: 0.9,
    6: 0.8,
    7: 0.7,
  };
  return table[clamped] ?? 1.0;
}

export const DEFAULT_WHEEL_CONFIG: WheelConfig = {
  enabled: true,
  presetId: 'hori_pro_dx',
  deviceName: 'HORI Mario Kart Racing Wheel Pro DX (NSX-123E)',
  gamepadIndex: 0,
  rotationDegrees: 270,
  sensitivityLevel: 4,
  paddleMode: 'blinkers',
  pedalMode: 'auto',
  steerAxis: 0,
  steerInvert: false,
  steerDeadzone: 0.02,
  steerLinearity: 1.0,
  throttle: { type: 'button', index: 7, invert: false }, // Right detachable pedal (ZR / Button 7 + auto-axis)
  throttleDeadzone: 0.02,
  brake: { type: 'button', index: 6, invert: false }, // Left detachable pedal (ZL / Button 6 + auto-axis)
  brakeDeadzone: 0.02,
  buttonGearUp: 3, // X button (or R-Paddle 5 in 'gears' mode)
  buttonGearDown: 0, // B button (or L-Paddle 4 in 'gears' mode)
  buttonBlinkerLeft: 4, // L-Paddle behind wheel
  buttonBlinkerRight: 5, // R-Paddle behind wheel
  buttonHazard: 13, // D-Pad Down
  buttonHandbrake: 10, // LSB / Left Stick Button
  buttonSeatbelt: 11, // RSB / Right Stick Button
  buttonEngine: 9, // + (Plus) Button
  buttonCamera: 8, // - (Minus) Button
  buttonHorn: 16, // HOME / Capture Button
  buttonShoulderLeft: 2, // Y Button (Left side of right spoke)
  buttonShoulderRight: 1, // A Button (Right side of right spoke)
  buttonLookInterior: 12, // D-Pad Up (Innenspiegel)
  buttonLookExteriorLeft: 14, // D-Pad Left (Linker Außenspiegel)
  buttonLookExteriorRight: 15, // D-Pad Right (Rechter Außenspiegel)
};

export const DEFAULT_KEY_MAP: KeyMap = {
  throttle: ['KeyW', 'ArrowUp'],
  brake: ['KeyS', 'ArrowDown'],
  steerLeft: ['ArrowLeft', 'KeyA'],
  steerRight: ['ArrowRight', 'KeyD'],
  handbrake: ['Space', 'KeyH'],  // Space = release only; H = full toggle (re-engage/release)
  clutch: ['KeyE'],          // Manual clutch pedal
  blinkerLeft: 'KeyY',
  blinkerRight: 'KeyC',
  hazard: 'KeyF',
  shoulderLeft: 'KeyZ',
  shoulderRight: 'KeyX',
  lookInterior: 'Digit1',
  lookExterior: 'Digit2',
  lookExteriorRight: 'Digit3',
  cameraToggle: 'KeyV',
  horn: 'KeyT',              // T = Tonhorn (horn moved from H to T)
  engineRestart: 'KeyI',    // Key I = ignition toggle (on/off)
  seatbelt: 'KeyB',         // Key B = buckle/unbuckle seatbelt
  gearUp: 'KeyR',           // Manual: upshift / Auto: cycles P→R→N→D
  gearDown: 'KeyG',         // Manual: downshift / Auto: cycles D→N→R→P (separate from clutch KeyE)
};

export function sanitizeKeyMap(keyMap: any): KeyMap {
  if (!keyMap || typeof keyMap !== 'object') {
    return { ...DEFAULT_KEY_MAP };
  }

  // Extract arrays with fallbacks to DEFAULT_KEY_MAP
  let throttle = Array.isArray(keyMap.throttle) ? [...keyMap.throttle] : [...DEFAULT_KEY_MAP.throttle];
  let brake = Array.isArray(keyMap.brake) ? [...keyMap.brake] : [...DEFAULT_KEY_MAP.brake];
  let steerLeft = Array.isArray(keyMap.steerLeft) ? [...keyMap.steerLeft] : [...DEFAULT_KEY_MAP.steerLeft];
  let steerRight = Array.isArray(keyMap.steerRight) ? [...keyMap.steerRight] : [...DEFAULT_KEY_MAP.steerRight];
  let handbrake = Array.isArray(keyMap.handbrake) ? [...keyMap.handbrake] : [...DEFAULT_KEY_MAP.handbrake];
  let clutch = Array.isArray(keyMap.clutch) ? [...keyMap.clutch] : [...DEFAULT_KEY_MAP.clutch];

  // Heal legacy / corrupted mappings:
  // 1. If brake contains 'KeyW', remove it immediately to prevent BOS / throttle conflict!
  brake = brake.filter((k: string) => k !== 'KeyW');
  if (!brake.includes('KeyS')) {
    brake.unshift('KeyS');
  }
  if (!brake.includes('ArrowDown')) {
    brake.push('ArrowDown');
  }

  // 2. Throttle must include 'KeyW' and 'ArrowUp', and purge legacy 'KeyQ'
  throttle = throttle.filter((k: string) => k !== 'KeyQ');
  if (!throttle.includes('KeyW')) {
    throttle.unshift('KeyW');
  }
  if (!throttle.includes('ArrowUp')) {
    throttle.push('ArrowUp');
  }

  // 3. Steer Left & Right
  if (!steerLeft.includes('ArrowLeft')) {
    steerLeft.unshift('ArrowLeft');
  }
  if (!steerLeft.includes('KeyA')) {
    steerLeft.push('KeyA');
  }
  if (!steerRight.includes('ArrowRight')) {
    steerRight.unshift('ArrowRight');
  }
  if (!steerRight.includes('KeyD')) {
    steerRight.push('KeyD');
  }

  // 4. Blinker Left/Right must not clash with steering keys KeyA or KeyD
  const blinkerLeft = typeof keyMap.blinkerLeft === 'string' && keyMap.blinkerLeft !== 'KeyA'
    ? keyMap.blinkerLeft
    : DEFAULT_KEY_MAP.blinkerLeft;
  const blinkerRight = typeof keyMap.blinkerRight === 'string' && keyMap.blinkerRight !== 'KeyD'
    ? keyMap.blinkerRight
    : DEFAULT_KEY_MAP.blinkerRight;
  const cameraToggle = typeof keyMap.cameraToggle === 'string' && keyMap.cameraToggle !== 'KeyC'
    ? keyMap.cameraToggle
    : DEFAULT_KEY_MAP.cameraToggle;
  const horn = typeof keyMap.horn === 'string' && keyMap.horn !== 'KeyH'
    ? keyMap.horn
    : DEFAULT_KEY_MAP.horn;

  return {
    throttle,
    brake,
    steerLeft,
    steerRight,
    handbrake: handbrake.length > 0 ? handbrake : [...DEFAULT_KEY_MAP.handbrake],
    clutch: clutch.length > 0 ? clutch : [...DEFAULT_KEY_MAP.clutch],
    blinkerLeft,
    blinkerRight,
    hazard: keyMap.hazard || DEFAULT_KEY_MAP.hazard,
    shoulderLeft: keyMap.shoulderLeft || DEFAULT_KEY_MAP.shoulderLeft,
    shoulderRight: keyMap.shoulderRight || DEFAULT_KEY_MAP.shoulderRight,
    lookInterior: keyMap.lookInterior || DEFAULT_KEY_MAP.lookInterior,
    lookExterior: keyMap.lookExterior || DEFAULT_KEY_MAP.lookExterior,
    lookExteriorRight: keyMap.lookExteriorRight || DEFAULT_KEY_MAP.lookExteriorRight,
    cameraToggle,
    horn,
    engineRestart: keyMap.engineRestart || DEFAULT_KEY_MAP.engineRestart,
    seatbelt: keyMap.seatbelt || DEFAULT_KEY_MAP.seatbelt,
    gearUp: keyMap.gearUp || DEFAULT_KEY_MAP.gearUp,
    gearDown: keyMap.gearDown || DEFAULT_KEY_MAP.gearDown,
  };
}

export interface InputState {
  throttle: number;
  brake: number;
  steering: number;
  handbrake: boolean;
  clutch: number;
  blinker: 'left' | 'right' | 'hazard' | 'off';
  shoulderCheck: 'left' | 'right' | null;
  mirrorLook: 'interior' | 'left' | 'right' | null;
  cameraToggle: boolean;
  horn: boolean;
  engineRestart: boolean;
  seatbeltToggle: boolean;
  seatbeltFastened: boolean;
  gearShiftUp: boolean;
  gearShiftDown: boolean;
  gearRequested: string | null;
  adjustCockpitToggle: boolean;
}

export class DesktopInput {
  public keyMap: KeyMap = { ...DEFAULT_KEY_MAP };
  public state: InputState = {
    throttle: 0,
    brake: 0,
    steering: 0,
    handbrake: true, // Handbrake starts latched ON when entering car
    clutch: 0,
    blinker: 'off',
    shoulderCheck: null,
    mirrorLook: null,
    cameraToggle: false,
    horn: false,
    engineRestart: false,
    seatbeltToggle: false,
    seatbeltFastened: false, // Seatbelt starts unfastened
    gearShiftUp: false,
    gearShiftDown: false,
    gearRequested: null,
    adjustCockpitToggle: false,
  };

  private keysDown: Set<string> = new Set();
  public handbrakeLatched: boolean = true;
  public seatbeltFastened: boolean = false;
  private brakeHoldDuration: number = 0;
  private steerHoldDuration: number = 0;
  private rawSteering: number = 0;
  private lastSteerTarget: number = 0;

  // Mouse Steering state
  public mouseSteeringActive: boolean = false;
  public mouseSteeringDragging: boolean = false;
  public mouseSteerTarget: number = 0;
  private mouseDragStartX: number = 0;
  public onMouseSteerStateChange: ((active: boolean) => void) | null = null;

  public toggleMouseSteering(forceValue?: boolean) {
    this.mouseSteeringActive = forceValue !== undefined ? forceValue : !this.mouseSteeringActive;
    if (!this.mouseSteeringActive) {
      this.mouseSteerTarget = 0;
      this.mouseSteeringDragging = false;
    }
    if (this.onMouseSteerStateChange) {
      this.onMouseSteerStateChange(this.mouseSteeringActive);
    }
  }

  public wheelConfig: WheelConfig = { ...DEFAULT_WHEEL_CONFIG };
  public wheelTelemetry: WheelTelemetry = {
    connected: false,
    deviceName: DEFAULT_WHEEL_CONFIG.deviceName,
    presetId: 'hori_pro_dx',
    steeringAngleDeg: 0,
    rawSteering: 0,
    throttlePct: 0,
    brakePct: 0,
    clutchPct: 0,
    rotationDegrees: 270,
    sensitivityLevel: 4,
    paddleMode: 'blinkers',
    pedalMode: 'auto',
  };
  private prevHatDown: boolean = false;

  constructor() {
    this.loadKeyMap();
    this.loadWheelConfig();
    if (typeof window !== 'undefined') {
      this.setupListeners();
    }
  }

  public applyWheelPreset(presetId: WheelPresetId) {
    if (presetId === 'hori_pro_dx') {
      this.wheelConfig = {
        ...DEFAULT_WHEEL_CONFIG,
        presetId: 'hori_pro_dx',
        deviceName: this.wheelConfig.deviceName || 'HORI Mario Kart Racing Wheel Pro DX (NSX-123E)',
        rotationDegrees: 270,
        sensitivityLevel: 4,
        steerLinearity: sensitivityLevelToLinearity(4),
        steerDeadzone: 0.02,
        paddleMode: 'blinkers',
        pedalMode: 'auto',
        throttle: { type: 'button', index: 7, invert: false },
        brake: { type: 'button', index: 6, invert: false },
        buttonGearUp: 3,
        buttonGearDown: 0,
        buttonBlinkerLeft: 4,
        buttonBlinkerRight: 5,
        buttonHazard: 13,
        buttonHandbrake: 10,
        buttonSeatbelt: 11,
        buttonEngine: 9,
        buttonCamera: 8,
        buttonHorn: 16,
        buttonShoulderLeft: 2,
        buttonShoulderRight: 1,
        buttonLookInterior: 12,
        buttonLookExteriorLeft: 14,
        buttonLookExteriorRight: 15,
      };
    } else if (presetId === 'logitech_g29') {
      this.wheelConfig = {
        ...DEFAULT_WHEEL_CONFIG,
        presetId: 'logitech_g29',
        deviceName: 'Logitech G29 / G920 Driving Force',
        rotationDegrees: 900,
        sensitivityLevel: 4,
        steerLinearity: 1.0,
        steerDeadzone: 0.015,
        paddleMode: 'gears',
        pedalMode: 'separate_axes',
        throttle: { type: 'axis', index: 2, invert: true, minVal: 1.0, maxVal: -1.0 },
        brake: { type: 'axis', index: 5, invert: true, minVal: 1.0, maxVal: -1.0 },
        clutch: { type: 'axis', index: 1, invert: true, minVal: 1.0, maxVal: -1.0 },
        buttonGearUp: 4,
        buttonGearDown: 5,
        buttonBlinkerLeft: 14,
        buttonBlinkerRight: 15,
      };
    } else if (presetId === 'thrustmaster') {
      this.wheelConfig = {
        ...DEFAULT_WHEEL_CONFIG,
        presetId: 'thrustmaster',
        deviceName: 'Thrustmaster T150 / T248 Racing Wheel',
        rotationDegrees: 270,
        sensitivityLevel: 4,
        steerLinearity: 1.0,
        steerDeadzone: 0.02,
        paddleMode: 'blinkers',
        pedalMode: 'separate_axes',
        throttle: { type: 'axis', index: 5, invert: true, minVal: 1.0, maxVal: -1.0 },
        brake: { type: 'axis', index: 1, invert: true, minVal: 1.0, maxVal: -1.0 },
      };
    } else {
      this.wheelConfig.presetId = 'custom';
    }
    this.saveWheelConfig(this.wheelConfig);
  }

  public setSensitivityLevel(level: number) {
    const clamped = Math.max(1, Math.min(7, Math.round(level)));
    this.wheelConfig.sensitivityLevel = clamped;
    this.wheelConfig.steerLinearity = sensitivityLevelToLinearity(clamped);
    this.saveWheelConfig(this.wheelConfig);
  }

  public setRotationDegrees(deg: 270 | 180 | 900) {
    this.wheelConfig.rotationDegrees = deg;
    this.saveWheelConfig(this.wheelConfig);
  }

  public setPaddleMode(mode: WheelPaddleMode) {
    this.wheelConfig.paddleMode = mode;
    if (mode === 'blinkers') {
      this.wheelConfig.buttonBlinkerLeft = 4;
      this.wheelConfig.buttonBlinkerRight = 5;
      this.wheelConfig.buttonGearUp = 3;
      this.wheelConfig.buttonGearDown = 0;
    } else {
      this.wheelConfig.buttonGearDown = 4;
      this.wheelConfig.buttonGearUp = 5;
      this.wheelConfig.buttonBlinkerLeft = 14;
      this.wheelConfig.buttonBlinkerRight = 15;
    }
    this.saveWheelConfig(this.wheelConfig);
  }

  public loadWheelConfig() {
    try {
      if (typeof localStorage === 'undefined') return;
      const saved = localStorage.getItem('fahrschule_wheel_config');
      if (saved) {
        this.wheelConfig = { ...DEFAULT_WHEEL_CONFIG, ...JSON.parse(saved) };
      }
    } catch {
      this.wheelConfig = { ...DEFAULT_WHEEL_CONFIG };
    }
  }

  public saveWheelConfig(newConfig: WheelConfig) {
    this.wheelConfig = { ...newConfig };
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fahrschule_wheel_config', JSON.stringify(this.wheelConfig));
      }
    } catch {
      // ignore
    }
  }

  public resetWheelConfig() {
    this.wheelConfig = { ...DEFAULT_WHEEL_CONFIG };
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('fahrschule_wheel_config');
    }
  }

  public loadKeyMap() {
    try {
      if (typeof localStorage === 'undefined') return;
      const saved = localStorage.getItem('fahrschule_keymap');
      if (saved) {
        this.keyMap = sanitizeKeyMap(JSON.parse(saved));
      }
    } catch {
      this.keyMap = { ...DEFAULT_KEY_MAP };
    }
  }

  public saveKeyMap(newMap: KeyMap) {
    this.keyMap = { ...newMap };
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fahrschule_keymap', JSON.stringify(this.keyMap));
      }
    } catch {
      // ignore
    }
  }

  public resetKeyMap() {
    this.keyMap = { ...DEFAULT_KEY_MAP };
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('fahrschule_keymap');
    }
  }

  private setupListeners() {
    window.addEventListener('keydown', (e) => {
      this.keysDown.add(e.code);

      // Prevent default scrolling/activation on driving hotkeys
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.code)) {
        e.preventDefault();
      }

      // Blinker toggles (Supports Q / E and Y / C for all international keyboards)
      // Activating left immediately cancels right, and vice-versa
      if (e.code === this.keyMap.blinkerLeft || e.code === 'KeyQ' || e.code === 'KeyY') {
        this.toggleBlinker('left');
      } else if (e.code === this.keyMap.blinkerRight || e.code === 'KeyE' || e.code === 'KeyC') {
        this.toggleBlinker('right');
      } else if (e.code === this.keyMap.hazard || e.code === 'KeyH') {
        this.toggleBlinker('hazard');
      } else if (e.code === this.keyMap.cameraToggle) {
        this.state.cameraToggle = true;
      } else if (e.code === this.keyMap.horn) {
        this.state.horn = true;
      } else if (e.code === this.keyMap.engineRestart) {
        this.state.engineRestart = true;
      } else if (e.code === this.keyMap.seatbelt) {
        this.toggleSeatbelt();
      } else if (e.code === 'Space') {
        // Space is RELEASE-ONLY — it only drops the handbrake, never re-engages it.
        // This prevents accidental re-engagement when users press Space a second time
        // after seeing the warning dialog. To re-engage use H or the HUD 🅿️ button.
        if (this.handbrakeLatched) {
          this.setHandbrake(false);
        }
      } else if (this.keyMap.handbrake.some((k) => k !== 'Space' && k === e.code)) {
        // Other dedicated handbrake keys (e.g. H) fully toggle engage / release
        this.toggleHandbrake();
      } else if (e.code === this.keyMap.gearUp && !e.repeat) {
        this.state.gearShiftUp = true;
      } else if (e.code === this.keyMap.gearDown && !e.repeat) {
        this.state.gearShiftDown = true;
      }

      // Toggle mouse steering mode with KeyM
      if (e.code === 'KeyM') {
        this.toggleMouseSteering();
      }

      // Quick Gear keys
      if (e.code === 'KeyP') this.state.gearRequested = 'P';
      if (e.code === 'KeyD') this.state.gearRequested = 'D';
      if (e.code === 'KeyN') this.state.gearRequested = 'N';
    });

    window.addEventListener('keyup', (e) => {
      this.keysDown.delete(e.code);
      if (e.code === this.keyMap.horn) this.state.horn = false;
    });

    // ── Mouse / Pointer Steering Listeners ──
    window.addEventListener('pointerdown', (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'BUTTON' ||
         target.tagName === 'INPUT' ||
         target.tagName === 'SELECT' ||
         (typeof target.closest === 'function' && (
           target.closest('#mobile-touch-hud') ||
           target.closest('.modal-overlay') ||
           target.closest('#hud-btn-settings') ||
           target.closest('#hud-btn-menu') ||
           target.closest('.touch-btn-action') ||
           target.closest('.examiner-dialog-card')
         )))
      ) {
        return;
      }
      if (e.button === 0) {
        this.mouseSteeringDragging = true;
        this.mouseDragStartX = e.clientX;
      }
    });

    window.addEventListener('pointermove', (e: PointerEvent) => {
      if (this.mouseSteeringDragging) {
        const deltaX = e.clientX - this.mouseDragStartX;
        const maxDist = 180; // ±180px gives full steering lock
        const raw = Math.max(-1.0, Math.min(1.0, deltaX / maxDist));
        this.mouseSteerTarget = Math.sign(raw) * Math.pow(Math.abs(raw), 1.5);
      } else if (this.mouseSteeringActive) {
        const screenCenterX = window.innerWidth / 2;
        const halfSpan = Math.max(200, window.innerWidth * 0.35); // 35% of half-screen to full lock
        const deltaX = e.clientX - screenCenterX;
        const raw = Math.max(-1.0, Math.min(1.0, deltaX / halfSpan));
        this.mouseSteerTarget = Math.sign(raw) * Math.pow(Math.abs(raw), 1.5);
      }
    });

    const releaseMouseSteer = () => {
      if (this.mouseSteeringDragging) {
        this.mouseSteeringDragging = false;
        this.mouseSteerTarget = 0;
      }
    };

    window.addEventListener('pointerup', releaseMouseSteer);
    window.addEventListener('pointercancel', releaseMouseSteer);
  }

  private onToggleHandbrakeCallback: ((latched: boolean) => void) | null = null;

  public onToggleHandbrake(cb: (latched: boolean) => void) {
    this.onToggleHandbrakeCallback = cb;
  }

  public toggleHandbrake() {
    this.handbrakeLatched = !this.handbrakeLatched;
    this.state.handbrake = this.handbrakeLatched;
    if (this.onToggleHandbrakeCallback) {
      this.onToggleHandbrakeCallback(this.handbrakeLatched);
    }
  }

  public setHandbrake(latched: boolean) {
    this.handbrakeLatched = latched;
    this.state.handbrake = latched;
    if (this.onToggleHandbrakeCallback) {
      this.onToggleHandbrakeCallback(this.handbrakeLatched);
    }
  }

  public onBlinkerChange: ((dir: 'left' | 'right' | 'hazard' | 'off') => void) | null = null;

  public toggleBlinker(dir: 'left' | 'right' | 'hazard') {
    // Mutual exclusion: activating one indicator cancels the other immediately
    this.state.blinker = this.state.blinker === dir ? 'off' : dir;
    if (this.onBlinkerChange) {
      this.onBlinkerChange(this.state.blinker);
    }
  }

  public setBlinker(dir: 'left' | 'right' | 'hazard' | 'off') {
    if (this.state.blinker === dir) return;
    this.state.blinker = dir;
    if (this.onBlinkerChange) {
      this.onBlinkerChange(this.state.blinker);
    }
  }

  public toggleSeatbelt() {
    this.seatbeltFastened = !this.seatbeltFastened;
    this.state.seatbeltFastened = this.seatbeltFastened;
    this.state.seatbeltToggle = true;
  }

  public setSeatbelt(fastened: boolean) {
    this.seatbeltFastened = fastened;
    this.state.seatbeltFastened = fastened;
  }

  public update(deltaTime: number) {
    // 1. Progressive Throttle (Human foot pedal ramp)
    const wantsThrottle = this.keyMap.throttle.some((k) => this.keysDown.has(k)) || this.keysDown.has('ArrowUp') || this.keysDown.has('KeyW');
    const targetThrottle = wantsThrottle ? 1.0 : 0.0;
    const throttleRate = wantsThrottle ? 7.0 : 6.0; // 0.18s to full throttle; quick lift-off
    this.state.throttle += (targetThrottle - this.state.throttle) * Math.min(1.0, deltaTime * throttleRate);

    // NOTE: Handbrake is NEVER auto-released. The student must explicitly release it
    // (Space or HUD button) as part of the Abfahrkontrolle pre-drive checklist.

    // 2. Progressive Brake (Human foot brake ramp)
    // Tapping or initial application begins with gentle comfort bite (~0.35, ~2.5 m/s²),
    // then builds progressively to full pedal bite (1.0, ~7.5-8.5 m/s² StVO § 34 stop)
    // over ~0.65s of continuous holding. Quick release allows easy speed modulation.
    const wantsBrake = this.keyMap.brake.some((k) => this.keysDown.has(k)) || this.keysDown.has('ArrowDown') || this.keysDown.has('KeyS');
    if (wantsBrake) {
      this.brakeHoldDuration += deltaTime;
      const holdRatio = Math.min(1.0, this.brakeHoldDuration / 0.65);
      const targetBrake = 0.35 + holdRatio * 0.65;
      this.state.brake += (targetBrake - this.state.brake) * Math.min(1.0, deltaTime * 7.5);
    } else {
      this.brakeHoldDuration = 0;
      this.state.brake += (0.0 - this.state.brake) * Math.min(1.0, deltaTime * 14.0);
      if (this.state.brake < 0.01) this.state.brake = 0;
    }

    // 3. Steering (ArrowLeft / ArrowRight & KeyA / KeyD) — Progressive Hesitant Onset & Gamma Curve
    let steerTarget = 0;
    if (this.keyMap.steerLeft.some((k) => this.keysDown.has(k)) || this.keysDown.has('ArrowLeft') || this.keysDown.has('KeyA')) steerTarget -= 1.0;
    if (this.keyMap.steerRight.some((k) => this.keysDown.has(k)) || this.keysDown.has('ArrowRight') || this.keysDown.has('KeyD')) steerTarget += 1.0;

    const keyboardSteerApplying = Math.abs(steerTarget) > 0.01;
    const mouseSteerApplying = Math.abs(this.mouseSteerTarget) > 0.01;

    if (keyboardSteerApplying) {
      // Keyboard input takes priority when keys are pressed
      if (Math.sign(steerTarget) !== Math.sign(this.lastSteerTarget)) {
        this.steerHoldDuration = 0;
        this.rawSteering = 0;
      }
      this.steerHoldDuration += deltaTime;
      this.lastSteerTarget = steerTarget;

      // Two-phase progressive rate: gentle 2.8 rad/s onset for micro-corrections (< 0.22s),
      // smoothly accelerating to 6.2 rad/s for sustained cornering
      const onsetProgress = Math.min(1.0, this.steerHoldDuration / 0.22);
      const activeRate = 2.8 + onsetProgress * 3.4;
      this.rawSteering += (steerTarget - this.rawSteering) * Math.min(1.0, deltaTime * activeRate);

      // Apply non-linear gamma curve (gamma = 1.65) for refined center precision
      const steerSign = Math.sign(this.rawSteering);
      const steerMag = Math.abs(this.rawSteering);
      this.state.steering = steerSign * Math.pow(steerMag, 1.65);
    } else if (mouseSteerApplying) {
      // Mouse steering actively applying (drag or mouse position)
      this.steerHoldDuration = 0;
      this.lastSteerTarget = 0;
      this.rawSteering = this.mouseSteerTarget;
      this.state.steering += (this.mouseSteerTarget - this.state.steering) * Math.min(1.0, deltaTime * 12.0);
    } else {
      this.steerHoldDuration = 0;
      this.lastSteerTarget = 0;
      // Controlled return rate for smooth, non-jerky self-centering
      this.state.steering += (0 - this.state.steering) * Math.min(1.0, deltaTime * 8.5);
      if (Math.abs(this.state.steering) < 0.01) {
        this.state.steering = 0;
        this.rawSteering = 0;
      } else {
        const s = Math.sign(this.state.steering);
        this.rawSteering = s * Math.pow(Math.abs(this.state.steering), 1 / 1.65);
      }
    }

    // 4. Handbrake
    this.state.handbrake = this.handbrakeLatched;

    // 5. Clutch
    const wantsClutch = this.keyMap.clutch.some((k) => this.keysDown.has(k));
    const targetClutch = wantsClutch ? 1.0 : 0.0;
    this.state.clutch += (targetClutch - this.state.clutch) * Math.min(1.0, deltaTime * 15);

    // 6. Shoulder Check
    if (this.keysDown.has(this.keyMap.shoulderLeft)) {
      this.state.shoulderCheck = 'left';
    } else if (this.keysDown.has(this.keyMap.shoulderRight)) {
      this.state.shoulderCheck = 'right';
    } else {
      this.state.shoulderCheck = null;
    }

    // 7. Mirror Look
    if (this.keysDown.has(this.keyMap.lookExteriorRight || 'Digit3')) this.state.mirrorLook = 'right';
    else if (this.keysDown.has(this.keyMap.lookExterior)) this.state.mirrorLook = 'left';
    else if (this.keysDown.has(this.keyMap.lookInterior)) this.state.mirrorLook = 'interior';
    else this.state.mirrorLook = null;

    this.state.seatbeltFastened = this.seatbeltFastened;

    this.pollGamepad(keyboardSteerApplying || mouseSteerApplying);
  }

  private prevPadButtons: boolean[] = [];

  /**
   * Reads normalized [0..1] pedal travel for detachable Gas, Brake, or Clutch pedals.
   * Supports:
   * - 'triggers' (ZR/ZL buttons[7]/buttons[6] analog or digital)
   * - 'separate_axes' (individual potentiometer axes with optional minVal/maxVal/invert)
   * - 'combined_axis' (single-cable 2-pedal unit sharing one axis, e.g. axes[1] resting at 0.0: <0 = Gas, >0 = Brake)
   * - 'auto' (HORI Pro DX smart mode: reads configured item AND auto-detects combined split axis or trigger travel)
   */
  public readPedalValue(pad: any, target: 'throttle' | 'brake' | 'clutch'): number {
    if (!pad) return 0;
    const cfg = this.wheelConfig;
    const item = target === 'throttle' ? cfg.throttle : target === 'brake' ? cfg.brake : cfg.clutch;
    const deadzone = target === 'throttle'
      ? (cfg.throttleDeadzone ?? 0.02)
      : target === 'brake'
      ? (cfg.brakeDeadzone ?? 0.02)
      : (cfg.clutchDeadzone ?? 0.02);

    if (!item) return 0;

    const applyDz = (val: number): number => {
      const clamped = Math.max(0, Math.min(1, val));
      return clamped > deadzone ? (clamped - deadzone) / (1 - deadzone) : 0;
    };

    // 1. Combined Single-Cable Axis Mode (e.g. 2 detachable pedals sharing axes[1] resting at 0.0)
    if (cfg.pedalMode === 'combined_axis' && target !== 'clutch') {
      const axisIdx = item.type === 'axis' ? item.index : 1;
      const raw = pad.axes?.[axisIdx] ?? 0;
      if (target === 'throttle') {
        const v = item.invert ? (raw > 0 ? raw : 0) : (raw < 0 ? -raw : 0);
        return applyDz(v);
      } else {
        const v = item.invert ? (raw < 0 ? -raw : 0) : (raw > 0 ? raw : 0);
        return applyDz(v);
      }
    }

    // 2. Read primary configured mapping (button or axis)
    let primaryVal = 0;
    if (item.type === 'button') {
      const btn = pad.buttons?.[item.index];
      const val = btn ? (typeof btn.value === 'number' ? btn.value : (btn.pressed ? 1 : 0)) : 0;
      primaryVal = applyDz(val);
    } else {
      const raw = pad.axes?.[item.index] ?? 0;
      let norm = 0;
      if (item.minVal !== undefined && item.maxVal !== undefined && Math.abs(item.maxVal - item.minVal) > 0.05) {
        norm = (raw - item.minVal) / (item.maxVal - item.minVal);
      } else if (item.invert) {
        // Inverted axis: +1.0 is unpressed, -1.0 is pressed
        norm = (1 - raw) / 2;
      } else {
        // Standard pedal axis: -1.0 is unpressed, +1.0 is pressed
        norm = (raw + 1) / 2;
      }
      primaryVal = applyDz(norm);
    }

    // 3. In 'auto' mode (HORI Pro DX default), if the primary trigger (ZR/ZL) is unpressed (0),
    // also check the detachable pedal axis (axes[1] combined split axis resting at 0.0)
    if (cfg.pedalMode === 'auto' && primaryVal === 0 && target !== 'clutch') {
      const combinedAxisRaw = pad.axes?.[1] ?? 0;
      if (target === 'throttle' && combinedAxisRaw < -deadzone) {
        primaryVal = applyDz(-combinedAxisRaw);
      } else if (target === 'brake' && combinedAxisRaw > deadzone) {
        primaryVal = applyDz(combinedAxisRaw);
      }
    }

    return primaryVal;
  }

  private pollGamepad(otherSteeringActive: boolean = false) {
    if (!this.wheelConfig.enabled) {
      this.wheelTelemetry.connected = false;
      return;
    }
    const gamepads = (typeof navigator !== 'undefined' && navigator.getGamepads) ? navigator.getGamepads() : [];
    const pad = gamepads[this.wheelConfig.gamepadIndex] || gamepads[0];
    if (!pad) {
      this.wheelTelemetry.connected = false;
      this.wheelTelemetry.steeringAngleDeg = Math.round(this.state.steering * ((this.wheelConfig.rotationDegrees || 270) / 2));
      this.wheelTelemetry.rawSteering = this.state.steering;
      this.wheelTelemetry.throttlePct = Math.round(this.state.throttle * 100);
      this.wheelTelemetry.brakePct = Math.round(this.state.brake * 100);
      this.wheelTelemetry.clutchPct = Math.round(this.state.clutch * 100);
      return;
    }

    if (pad.id && this.wheelConfig.deviceName !== pad.id) {
      this.wheelConfig.deviceName = pad.id;
      if (/hori|nsx-123|nsw-228|mario kart|racing wheel/i.test(pad.id) && !this.wheelConfig.presetId) {
        this.wheelConfig.presetId = 'hori_pro_dx';
      }
    }

    const cfg = this.wheelConfig;
    const rotDeg = cfg.rotationDegrees || 270;

    // 1. Steering Axis (configurable index, invert, deadzone & HORI 7-level sensitivity / linearity)
    const rawSteer = pad.axes[cfg.steerAxis] ?? 0;
    const steerDir = cfg.steerInvert ? -1 : 1;
    const absSteer = Math.abs(rawSteer);
    if (absSteer > cfg.steerDeadzone) {
      const norm = (absSteer - cfg.steerDeadzone) / (1 - cfg.steerDeadzone);
      const sign = Math.sign(rawSteer) * steerDir;
      const linearity = cfg.steerLinearity || sensitivityLevelToLinearity(cfg.sensitivityLevel || 4);
      // In 180° mode, reach full steering lock at ±90° (1.5x quicker ratio than 270°)
      const modeScale = rotDeg === 180 ? 1.25 : 1.0;
      this.state.steering = Math.max(-1, Math.min(1, sign * Math.pow(Math.max(0, norm), linearity) * modeScale));
      this.rawSteering = this.state.steering;
    } else if (!otherSteeringActive) {
      // Physical wheel is centered inside deadzone — immediately center steering
      this.state.steering = 0;
      this.rawSteering = 0;
    }

    // 2. Detachable Gas Pedal (Throttle)
    const throttleVal = this.readPedalValue(pad, 'throttle');
    if (throttleVal > 0.01) {
      this.state.throttle = Math.max(this.state.throttle, throttleVal);
    }

    // 3. Detachable Brake Pedal (Brake)
    const brakeVal = this.readPedalValue(pad, 'brake');
    if (brakeVal > 0.01) {
      this.state.brake = Math.max(this.state.brake, brakeVal);
    }

    // 4. Clutch (if configured)
    let clutchVal = 0;
    if (cfg.clutch) {
      clutchVal = this.readPedalValue(pad, 'clutch');
      if (clutchVal > 0.01) {
        this.state.clutch = Math.max(this.state.clutch, clutchVal);
      }
    }

    // Update live telemetry for HUD widget
    this.wheelTelemetry = {
      connected: true,
      deviceName: cfg.deviceName || pad.id || 'HORI Racing Wheel Pro DX',
      presetId: cfg.presetId || 'hori_pro_dx',
      steeringAngleDeg: Math.round(this.state.steering * (rotDeg / 2)),
      rawSteering: this.state.steering,
      throttlePct: Math.round(Math.max(this.state.throttle, throttleVal) * 100),
      brakePct: Math.round(Math.max(this.state.brake, brakeVal) * 100),
      clutchPct: Math.round(Math.max(this.state.clutch, clutchVal) * 100),
      rotationDegrees: rotDeg,
      sensitivityLevel: cfg.sensitivityLevel || 4,
      paddleMode: cfg.paddleMode || 'blinkers',
      pedalMode: cfg.pedalMode || 'auto',
    };

    // 5. Buttons & Paddles with edge-triggering
    const isPressed = (idx?: number) => idx !== undefined && !!pad.buttons[idx]?.pressed;
    const wasJustPressed = (idx?: number) => {
      if (idx === undefined) return false;
      const curr = !!pad.buttons[idx]?.pressed;
      const prev = !!this.prevPadButtons[idx];
      return curr && !prev;
    };

    // Paddles & Gear / Blinker routing based on paddleMode ('blinkers' vs 'gears')
    if (cfg.paddleMode === 'gears') {
      if (wasJustPressed(5) || wasJustPressed(cfg.buttonGearUp)) {
        this.state.gearShiftUp = true;
        this.state.gearRequested = 'D';
      }
      if (wasJustPressed(4) || wasJustPressed(cfg.buttonGearDown)) {
        this.state.gearShiftDown = true;
      }
      if (wasJustPressed(cfg.buttonBlinkerLeft) && cfg.buttonBlinkerLeft !== 4) {
        this.toggleBlinker('left');
      }
      if (wasJustPressed(cfg.buttonBlinkerRight) && cfg.buttonBlinkerRight !== 5) {
        this.toggleBlinker('right');
      }
    } else {
      // Default 'blinkers' mode: L-Paddle (4) = Blinker Left, R-Paddle (5) = Blinker Right
      if (wasJustPressed(cfg.buttonBlinkerLeft ?? 4)) this.toggleBlinker('left');
      if (wasJustPressed(cfg.buttonBlinkerRight ?? 5)) this.toggleBlinker('right');
      if (wasJustPressed(cfg.buttonGearUp ?? 3)) {
        this.state.gearShiftUp = true;
        this.state.gearRequested = 'D';
      }
      if (wasJustPressed(cfg.buttonGearDown ?? 0)) {
        this.state.gearShiftDown = true;
      }
    }

    // D-Pad / Hat Switch Observation Checks (Innenspiegel, Außenspiegel L/R, Warnblinker)
    const hatX = pad.axes.length >= 7 ? (pad.axes[6] ?? 0) : 0;
    const hatY = pad.axes.length >= 8 ? (pad.axes[7] ?? 0) : 0;
    const lookInteriorActive = isPressed(cfg.buttonLookInterior ?? 12) || hatY < -0.5;
    const lookLeftActive = isPressed(cfg.buttonLookExteriorLeft ?? 14) || hatX < -0.5;
    const lookRightActive = isPressed(cfg.buttonLookExteriorRight ?? 15) || hatX > 0.5;
    const hazardHatDown = hatY > 0.5;

    if (lookInteriorActive) {
      this.state.mirrorLook = 'interior';
    } else if (lookLeftActive) {
      this.state.mirrorLook = 'left';
    } else if (lookRightActive) {
      this.state.mirrorLook = 'right';
    }

    if (wasJustPressed(cfg.buttonHazard ?? 13) || (hazardHatDown && !this.prevHatDown)) {
      this.toggleBlinker('hazard');
    }
    this.prevHatDown = hazardHatDown;

    // Shoulder Checks (Y = Left Shoulder Check, A = Right Shoulder Check)
    if (isPressed(cfg.buttonShoulderLeft ?? 2)) {
      this.state.shoulderCheck = 'left';
    } else if (isPressed(cfg.buttonShoulderRight ?? 1)) {
      this.state.shoulderCheck = 'right';
    }

    // Handbrake toggle (LSB / Button 10)
    if (wasJustPressed(cfg.buttonHandbrake)) this.toggleHandbrake();

    // Seatbelt toggle (RSB / Button 11)
    if (wasJustPressed(cfg.buttonSeatbelt)) this.toggleSeatbelt();

    // Engine Start / Ignition (+ / Button 9)
    if (wasJustPressed(cfg.buttonEngine ?? 9)) this.state.engineRestart = true;

    // Horn (HOME / Button 16)
    if (isPressed(cfg.buttonHorn)) this.state.horn = true;

    // Camera toggle (- / Button 8)
    if (wasJustPressed(cfg.buttonCamera)) this.state.cameraToggle = true;

    // Save button states for edge detection
    for (let i = 0; i < pad.buttons.length; i++) {
      this.prevPadButtons[i] = !!pad.buttons[i]?.pressed;
    }
  }

  public consumeCameraToggle(): boolean {
    const val = this.state.cameraToggle;
    this.state.cameraToggle = false;
    return val;
  }

  public consumeEngineRestart(): boolean {
    const val = this.state.engineRestart;
    this.state.engineRestart = false;
    return val;
  }

  public consumeGearRequest(): string | null {
    const val = this.state.gearRequested;
    this.state.gearRequested = null;
    return val;
  }

  public consumeGearShiftUp(): boolean {
    const val = this.state.gearShiftUp;
    this.state.gearShiftUp = false;
    return val;
  }

  public consumeGearShiftDown(): boolean {
    const val = this.state.gearShiftDown;
    this.state.gearShiftDown = false;
    return val;
  }

  public consumeSeatbeltToggle(): boolean {
    const val = this.state.seatbeltToggle;
    this.state.seatbeltToggle = false;
    return val;
  }
}
